import type { Theme } from 'src/themeMui5/type';

/**
 * Status Colors: info, success, warning, error
 */

export type SupportedStatusColor = 'info' | 'warning' | 'error' | 'success';
function isSupportedStatusColor(color: string | undefined): color is SupportedStatusColor {
  if (!color) {
    return false;
  }
  return ['info', 'warning', 'error', 'success'].includes(color);
}

function getStatusColorFromTheme(color: SupportedStatusColor, theme: Theme): string {
  return theme.palette[color].main;
}

/**
 * Function Colors: primary, secondary
 */

export type SupportedFunctionColor = 'primary' | 'secondary' | 'tertiary' | 'pending';
function isSupportedFunctionColor(color: string | undefined): color is SupportedStatusColor {
  if (!color) {
    return false;
  }
  return ['primary', 'secondary', 'tertiary', 'pending'].includes(color);
}

/**
 * All Colors: primary, secondary, info, success, warning, error
 */

export type SupportedColor = SupportedFunctionColor | SupportedStatusColor;
export function isSupportedColor(color: string | undefined): color is SupportedColor {
  return isSupportedStatusColor(color) || isSupportedFunctionColor(color);
}

export function getColorFromTheme(color: SupportedColor, theme: Theme): string {
  return theme.palette[color].main;
}

/**
 * All Greys: max, high, mid, low, min
 */

export type SupportedGrey =
  | 'max-contrast-grey'
  | 'high-contrast-grey'
  | 'mid-contrast-grey'
  | 'low-contrast-grey'
  | 'min-contrast-grey';

export function isSupportedGrey(grey: string | undefined): grey is SupportedGrey {
  if (!grey) {
    return false;
  }
  return [
    'max-contrast-grey',
    'high-contrast-grey',
    'mid-contrast-grey',
    'low-contrast-grey',
    'min-contrast-grey',
  ].includes(grey);
}

export function getGreyFromTheme(grey: SupportedGrey, theme: Theme): string {
  switch (grey) {
    default:
    case 'max-contrast-grey':
      return theme.grey.alpha.max;
    case 'high-contrast-grey':
      return theme.grey.alpha.high;
    case 'mid-contrast-grey':
      return theme.grey.alpha.mid;
    case 'low-contrast-grey':
      return theme.grey.alpha.low;
    case 'min-contrast-grey':
      return theme.grey.alpha.min;
  }
}

/**
 * All Greys: max, high, mid, low, min
 */

export type SupportedTextColor = 'inherit' | 'foreground' | 'background' | SupportedStatusColor | SupportedGrey;
export type SupportedBackgroundColor =
  | 'default'
  | 'paper'
  | 'alternate'
  | 'contrast'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'pending';

export function isSupportedTextColor(color: string | undefined): color is SupportedTextColor {
  if (!color) {
    return false;
  }
  return (
    isSupportedStatusColor(color) ||
    ['max-contrast-grey', 'high-contrast-grey', 'mid-contrast-grey', 'foreground', 'background'].includes(color)
  );
}

export function getTextColorFromTheme(color: SupportedTextColor, theme: Theme): string {
  if (isSupportedGrey(color)) {
    return getGreyFromTheme(color, theme);
  }

  if (isSupportedStatusColor(color)) {
    return getStatusColorFromTheme(color, theme);
  }

  if (color === 'inherit') {
    return 'inherit';
  }

  if (color === 'background') {
    return theme.palette.background.default;
  }

  // foreground
  return theme.palette.text.primary;
}

export function getBackgroundColorFromTheme(color: SupportedBackgroundColor, theme: Theme): string {
  return theme.palette.background[color] ?? theme.palette.background.default;
}
